/**
 * OtherKeyCard — works like SpareRadioCard.
 *
 * Two indicators:
 *   1. Available — green dot when key is in the rack.
 *                  Click → inline text input to enter assignee name.
 *                  Type name + Enter or ✓ → goes blue/orange with name.
 *                  Click name → returns key to rack.
 *   2. Not Available — red dot. Click to mark missing. Click again to clear.
 */
import { useState, useRef, useEffect } from 'react'
import { Card, StatusDot, dotLabelColor } from '../ui'
import { KEY_ICON } from '../../assets'
import { markDirty } from '../../stores/sync'
import type { OtherKey } from '../../stores/desktop'

interface OtherKeyCardProps {
  k: OtherKey
  onUpdate: (patch: Partial<OtherKey>) => void
}

function lastName(name: string): string {
  const s = name.replace(/\s*\(.*?\)\s*/g, '').trim()
  return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
}

export default function OtherKeyCard({ k, onUpdate }: OtherKeyCardProps) {
  const [editing, setEditing] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const isOos      = !!k.oos
  const isAssigned = !isOos && !!k.assignedTo && k.available
  const isNotAvail = !isOos && !k.available && !k.assignedTo
  const isAvail    = !isOos && k.available && !k.assignedTo

  const borderColor = isOos      ? '#fca5a5'
                    : isAssigned ? '#3b82f6'
                    : isNotAvail ? '#f87171'
                    : editing    ? '#60a5fa'
                                 : '#4ade80'

  const imgActive = isAssigned || isAvail || editing

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  function handleAvailableClick() {
    if (isOos) return
    if (isAssigned) {
      markDirty()
      onUpdate({ available: true, assignedTo: '' })
    } else if (isAvail && !editing) {
      setInputVal('')
      setEditing(true)
    } else if (isNotAvail) {
      markDirty()
      onUpdate({ available: true, assignedTo: '' })
    }
  }

  function commitAssign() {
    const name = inputVal.trim()
    setEditing(false)
    setInputVal('')
    if (name) {
      markDirty()
      onUpdate({ available: true, assignedTo: name })
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitAssign()
    if (e.key === 'Escape') { setEditing(false); setInputVal('') }
  }

  function handleNotAvail() {
    if (isOos) return
    setEditing(false)
    setInputVal('')
    markDirty()
    if (isNotAvail) {
      onUpdate({ available: true, assignedTo: '' })
    } else {
      onUpdate({ available: false, assignedTo: '' })
    }
  }

  return (
    <Card variant="item" borderColor={borderColor} opacity={isOos ? 0.5 : 1}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: 'var(--text-muted)',
        marginBottom: 6, textAlign: 'center',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
        title={`${k.number} ${k.description}`}
      >
        {k.number} {k.description}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flexShrink: 0, width: 60, height: 60 }}>
          <img src={KEY_ICON} alt=""
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              opacity: imgActive ? 1 : 0.4,
              filter:  imgActive ? 'none' : 'grayscale(100%)',
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {/* Row 1 — Available / Assigned */}
              {editing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                  <input
                    ref={inputRef}
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={commitAssign}
                    placeholder="Name"
                    style={{
                      flex: 1, minWidth: 0,
                      background: 'var(--bg-deep)',
                      border: '1px solid #60a5fa',
                      borderRadius: 4,
                      color: 'var(--text-primary)',
                      fontSize: 11, padding: '2px 6px', outline: 'none',
                    }}
                  />
                  <span
                    onMouseDown={e => { e.preventDefault(); commitAssign() }}
                    style={{ fontSize: 13, cursor: 'pointer', color: '#60a5fa', fontWeight: 700 }}
                  >
                    ✓
                  </span>
                </div>
              ) : (
                <div
                  onClick={handleAvailableClick}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    cursor: 'pointer', opacity: isAvail || isAssigned ? 1 : 0.3,
                  }}
                >
                  <StatusDot variant={isAssigned ? 'blue' : 'green'} active={isAvail || isAssigned} />
                  {isAssigned ? (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: '#1d4ed8',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {lastName(k.assignedTo)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('green', isAvail) }}>
                      Available
                    </span>
                  )}
                </div>
              )}

              {/* Row 2 — Not Available */}
              <div
                onClick={handleNotAvail}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  cursor: 'pointer', opacity: isNotAvail ? 1 : 0.3,
                }}
              >
                <StatusDot variant="red" active={isNotAvail} />
                <span style={{ fontSize: 11, fontWeight: 700, color: dotLabelColor('red', isNotAvail) }}>
                  Not Avail.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
